import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ShopCart from '~/backend/shop_cart';
import { formatAmountValue } from '~/common/helper';
import Button from '~/components/common/Button';
import Modal from '~/components/common/Modal';

// Server-side DiscountCapConstraint enum values; kept inline (rather than imported
// from the generated TS client) so the popup stays self-contained.
const CAP_CONSTRAINT_MAX_PERCENTAGE = 1;
const CAP_CONSTRAINT_MAX_AMOUNT = 2;
const CAP_CONSTRAINT_MIN_FINAL_PRICE = 3;

const formatCapRule = (t, capRuleType, capRuleVal) => {
	if (capRuleVal === null || capRuleVal === undefined) return null;

	switch (capRuleType) {
		case CAP_CONSTRAINT_MAX_PERCENTAGE:
			return t('applied_discounts_cap_rule_max_percentage', { value: capRuleVal });
		case CAP_CONSTRAINT_MAX_AMOUNT:
			return t('applied_discounts_cap_rule_max_amount', { value: formatAmountValue(capRuleVal) });
		case CAP_CONSTRAINT_MIN_FINAL_PRICE:
			return t('applied_discounts_cap_rule_min_final_price', { value: formatAmountValue(capRuleVal) });
		default:
			return null;
	}
};

// Collapse the per-unit items[] coming from the server into one visual row per
// (ln, cap_id) pair. Multi-quantity lines (one CheckoutItemContext per unit)
// thus render as a single row with summed amounts.
const groupItemsForDisplay = items => {
	const groups = new Map();
	for (const item of items) {
		const key = `${item.ln}|${item.cap_id ?? 'none'}`;
		const existing = groups.get(key);
		if (existing) {
			existing.amn += item.amn ?? 0;
			existing.oamn = existing.oamn === null ? (item.oamn ?? null) : existing.oamn + (item.oamn ?? item.amn ?? 0);
			existing.qty += 1;
		} else {
			groups.set(key, {
				ln: item.ln,
				pid: item.pid,
				pdid: item.pdid,
				amn: item.amn ?? 0,
				oamn: item.oamn ?? null,
				cap_id: item.cap_id ?? null,
				cap_rule_type: item.cap_rule_type ?? null,
				cap_rule_val: item.cap_rule_val ?? null,
				qty: 1
			});
		}
	}
	return Array.from(groups.values());
};

const AppliedDiscountsModal = ({ appliedDiscounts, open, setOpen }) => {
	const { t } = useTranslation();

	const products = ShopCart.getItems();

	return (
		<div className="discounts-poupup-modal">
			<Modal open={open} setOpen={setOpen} className="discounts-popup">
				<div className="discounts-popup-container">
					<div className="header">
						<div className="title">{t('discounts_applied')}</div>
					</div>
					<div className="body">
						{/* <h4>Συμπλήρωσε το email σου για να κερδίσεις:</h4> */}
						<ul className="discounts-features">
							{appliedDiscounts?.map(({ dt, cd, ln, oamn, odamt, amn, descr, has_capped, items }, index) => {
								const itemGroups = Array.isArray(items) && items.length > 0 ? groupItemsForDisplay(items) : [];
								return (
									<li key={index}>
										<div className="row-header">
											<h5>
												{t(dt)} {cd ? <b>{cd}</b> : <b>{products[ln - 1]?.product?.nm}</b>}
												{descr ? <b> {descr}</b> : null}
											</h5>
											{amn > 0 && (
												<div className="row-header-amounts">
													{odamt > 0 && odamt > amn && <span className="amount-original">{formatAmountValue(odamt)}</span>}
													<span>{formatAmountValue(amn)}</span>
												</div>
											)}
										</div>
										{oamn > 0 && (
											<div className="row-oamt">
												<small>Διαθέσιμο Ποσό: {formatAmountValue(oamn)}</small>
											</div>
										)}
										{has_capped && (
											<div className="row-capped">
												<small>{t('applied_discounts_cap_notice')}</small>
											</div>
										)}
										{itemGroups.length > 0 && (
											<ul className="discounts-items">
												{itemGroups.map((group, groupIndex) => {
													const capRuleText = group.cap_id !== null ? formatCapRule(t, group.cap_rule_type, group.cap_rule_val) : null;
													const showAmountPair = group.oamn !== null && group.oamn > group.amn;
													return (
														<li
															key={groupIndex}
															className={group.cap_id !== null ? 'discounts-item discounts-item--capped' : 'discounts-item'}>
															<div className="discounts-item__row">
																<span className="discounts-item__name">{products[group.ln - 1]?.product?.nm}</span>
																{group.amn > 0 && (
																	<div className="discounts-item__amounts">
																		{showAmountPair && <span className="amount-original">{formatAmountValue(group.oamn)}</span>}
																		<span>{formatAmountValue(group.amn)}</span>
																	</div>
																)}
															</div>
															{capRuleText && (
																<div className="discounts-item__rule">
																	<small>{capRuleText}</small>
																</div>
															)}
														</li>
													);
												})}
											</ul>
										)}
									</li>
								);
							})}
						</ul>
					</div>

					<div className="footer">
						<Button color="secondary" actionOnClick={() => setOpen(false)}>
							{t('close')}
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	);
};

AppliedDiscountsModal.propTypes = {
	appliedDiscounts: PropTypes.array,
	open: PropTypes.bool,
	setOpen: PropTypes.func
};

export default AppliedDiscountsModal;
